/* Brightivity UI — every building block of the App UI Kit (§ 04 Components) as a React component.
 * Screens are assembled ONLY from these; styles live in components.css (tokens.css values). */
import { useEffect, useId, useRef } from 'react';
import { ICONS } from './icons.generated.js';
import './base.css';
import './components.css';
import i18n from '../i18n/index.js';

const cx = (...a) => a.filter(Boolean).join(' ');

/* ------------------------------------------------------------------ Icon (§ 05: 41 line icons) */
export const ICON_NAMES = Object.keys(ICONS);
// Brand marks that are not handoff line icons (kept out of the generated file). Canva: its circle-with-"C" mark drawn
// as a line icon — currentColor strokes only, like the other 41 — so it takes the sidebar / tile / button colour.
const BRAND_ICONS = {
  canva: '<circle cx="12" cy="12" r="9.5"/>'
    + '<path d="M15.6 9.2c-.8-1.3-2.1-2.1-3.6-2.1-2.7 0-4.8 2.2-4.8 4.9s2.1 4.9 4.8 4.9c1.5 0 2.8-.8 3.6-2" stroke-width="2.6"/>',
};
export function Icon({ name, size = 24, className, title, ...rest }) {
  const body = ICONS[name] || BRAND_ICONS[name];
  if (!body) throw new Error(`Unknown Brightivity icon "${name}"`);
  return (
    <svg
      className={cx('bv-icon', className)} width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden={title ? undefined : true} role={title ? 'img' : undefined} {...rest}
      dangerouslySetInnerHTML={{ __html: (title ? `<title>${title}</title>` : '') + body }}
    />
  );
}

/* ------------------------------------------------------------------ Buttons */
export function Button({ variant = 'secondary', size, block, icon, iconRight, children, className, as: As = 'button', ...rest }) {
  const iconSize = size === 'big' ? 22 : size === 'sm' ? 16 : 20;
  return (
    <As className={cx('bv-btn', `bv-btn--${variant}`, size && `bv-btn--${size}`, block && 'bv-btn--block', className)}
      {...(As === 'button' ? { type: rest.type || 'button' } : {})} {...rest}>
      {icon && <Icon name={icon} size={iconSize} />}
      {children}
      {iconRight && <Icon name={iconRight} size={iconSize} />}
    </As>
  );
}

export function IconButton({ icon, label, className, ...rest }) {
  return (
    <button type="button" className={cx('bv-iconbtn', className)} aria-label={label} title={label} {...rest}>
      <Icon name={icon} size={22} />
    </button>
  );
}

/* ------------------------------------------------------------------ Chip, Tag */
export function Chip({ selected, dot, count, children, className, ...rest }) {
  return (
    <button type="button" className={cx('bv-chip', selected && 'bv-chip--selected', className)} aria-pressed={!!selected} {...rest}>
      {dot && <span className={cx('bv-chip__dot', `bv-dot--${dot}`)} />}
      {children}
      {count != null && <span className="bv-chip__count">{count}</span>}
    </button>
  );
}

export function Tag({ color = 'blue', children, className, ...rest }) {
  return <span className={cx('bv-tag', `bv-tag--${color}`, className)} {...rest}>{children}</span>;
}

/* ------------------------------------------------------------------ Inputs */
export function Input({ className, ...rest }) {
  return <input className={cx('bv-input', className)} {...rest} />;
}

export function SearchInput({ className, ...rest }) {
  return (
    <div className={cx('bv-field', 'bv-field--search', className)}>
      <Icon name="search" size={20} className="bv-field__icon" />
      <input type="search" className="bv-input" {...rest} />
    </div>
  );
}

export function Select({ options, className, ...rest }) {
  return (
    <div className={cx('bv-field', 'bv-field--select', className)}>
      <select className="bv-select" {...rest}>
        {options.map((o) => (typeof o === 'string' ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>))}
      </select>
      <Icon name="down" size={20} className="bv-field__chevron" />
    </div>
  );
}

export function Textarea({ maxLength, value = '', className, ...rest }) {
  return (
    <div className={cx('bv-field', 'bv-field--textarea', className)}>
      <textarea className="bv-textarea" maxLength={maxLength} value={value} {...rest} />
      {maxLength && <span className="bv-field__counter">{value.length} / {maxLength}</span>}
    </div>
  );
}

/* ------------------------------------------------------------------ Choice pills, toggle */
export function ChoicePills({ options, value, onChange, compact, label, className }) {
  return (
    <div className={cx('bv-choices', compact && 'bv-choices--compact', className)} role="radiogroup" aria-label={label}>
      {options.map((o) => {
        const v = typeof o === 'string' ? o : o.value;
        const l = typeof o === 'string' ? o : o.label;
        return (
          <button key={v} type="button" role="radio" aria-checked={value === v} className="bv-choice" onClick={() => onChange && onChange(v)}>{l}</button>
        );
      })}
    </div>
  );
}

export function Toggle({ checked, onChange, children, className }) {
  return (
    <button type="button" role="switch" aria-checked={!!checked} className={cx('bv-toggle', className)} onClick={() => onChange && onChange(!checked)}>
      <span className="bv-toggle__track"><span className="bv-toggle__thumb" /></span>
      {children}
    </button>
  );
}

/* ------------------------------------------------------------------ Tabs, segmented */
export function Tabs({ tabs, value, onChange, className, label }) {
  return (
    <div className={cx('bv-tabs', className)} role="tablist" aria-label={label}>
      {tabs.map((t) => {
        const v = typeof t === 'string' ? t : t.value;
        const l = typeof t === 'string' ? t : t.label;
        return <button key={v} type="button" role="tab" aria-selected={value === v} className="bv-tab" onClick={() => onChange && onChange(v)}>{l}</button>;
      })}
    </div>
  );
}

export function Segmented({ options, value, onChange, className }) {
  return (
    <div className={cx('bv-segmented', className)} role="tablist">
      {options.map((o) => (
        <button key={o.value} type="button" role="tab" aria-selected={value === o.value} onClick={() => onChange && onChange(o.value)}>{o.label}</button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ Card, panel, icon tile */
export function Card({ pad = true, className, as: As = 'div', ...rest }) {
  return <As className={cx('bv-card', pad && 'bv-card--pad', className)} {...rest} />;
}
export function Panel({ className, ...rest }) {
  return <div className={cx('bv-panel', className)} {...rest} />;
}
export function IconTile({ icon, color = 'blue', size = 52, iconSize = 26, className }) {
  return (
    <span className={cx('bv-icontile', `bv-icontile--${color}`, className)} style={size !== 52 ? { width: size, height: size } : undefined}>
      <Icon name={icon} size={iconSize} />
    </span>
  );
}

/* ------------------------------------------------------------------ Tool tile */
export function ToolTile({ color = 'blue', icon, title, subtitle, children, className, as: As = 'button', ...rest }) {
  return (
    <As className={cx('bv-tile', `bv-tile--${color}`, className)} {...(As === 'button' ? { type: 'button' } : {})} {...rest}>
      <span className="bv-tile__blob" aria-hidden="true" />
      {!children && <Icon name={icon} size={120} className="bv-tile__ghost" />}
      <span className="bv-tile__head">
        <span className="bv-tile__badge"><Icon name={icon} size={26} /></span>
        <span>
          <span className="bv-tile__title" style={{ display: 'block' }}>{title}</span>
          {subtitle && <span className="bv-tile__sub" style={{ display: 'block' }}>{subtitle}</span>}
        </span>
      </span>
      {children && <span className="bv-tile__body">{children}</span>}
    </As>
  );
}

/* ------------------------------------------------------------------ Sidebar item */
export function NavItem({ icon, label, active, badge, as: As = 'button', ...rest }) {
  return (
    <As className="bv-navitem" aria-current={active ? 'page' : undefined} {...(As === 'button' ? { type: 'button' } : {})} {...rest}>
      <span className="bv-navitem__box">
        <Icon name={icon} size={24} />
        {badge ? <span className="bv-navitem__badge">{badge}</span> : null}
      </span>
      <span>{label}</span>
    </As>
  );
}

/* ------------------------------------------------------------------ Progress, ink */
export function ProgressBar({ value = 0, max = 100, label, className }) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div className={cx('bv-progress', className)} role="progressbar" aria-valuemin={0} aria-valuemax={max} aria-valuenow={value} aria-label={label}>
      <div className="bv-progress__bar" style={{ width: `${pct}%` }} />
    </div>
  );
}

const INK = { C: '#1AB0F2', M: '#E5179B', Y: '#FFD600', K: '#1B2550' };
export function InkRing({ channel = 'C', percent = 0, size = 68, stroke = 7, color, label }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, percent));
  return (
    <div className="bv-inkring">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`${label || channel} ${pct}%`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#EEF0F6" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color || INK[channel] || INK.K} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={`${(c * pct) / 100} ${c}`} transform={`rotate(-90 ${size / 2} ${size / 2})`} />
        <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle" className="bv-inkring__pct">{pct}%</text>
      </svg>
      <span className="bv-inkring__name" title={label}>{channel}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ Stat card, option card */
export function StatCard({ icon, color, value, label, className }) {
  return (
    <Card pad={false} className={cx('bv-stat', className)}>
      <IconTile icon={icon} color={color} />
      <div>
        <div className="bv-stat__value">{value}</div>
        <div className="bv-stat__label">{label}</div>
      </div>
    </Card>
  );
}

export function OptionCard({ icon, color, title, subtitle, checked, onClick, className }) {
  return (
    <button type="button" role="radio" aria-checked={!!checked} className={cx('bv-option', className)} onClick={onClick}>
      <IconTile icon={icon} color={color} />
      <span>
        <span className="bv-option__title" style={{ display: 'block' }}>{title}</span>
        {subtitle && <span className="bv-option__sub" style={{ display: 'block' }}>{subtitle}</span>}
      </span>
      {checked && <span className="bv-option__check"><Icon name="check" size={16} /></span>}
    </button>
  );
}

/* ------------------------------------------------------------------ Avatar, credits */
export function Avatar({ initials, ...rest }) {
  return <button type="button" className="bv-avatar" aria-label={i18n.t('bv:shell.yourAccount')} {...rest}>{initials}</button>;
}
export function CreditsPill({ credits, ...rest }) {
  return (
    <button type="button" className="bv-credits" {...rest}>
      <Icon name="sparkle" size={20} />
      {i18n.t('bv:common.credits', { count: credits })}
    </button>
  );
}

/* ------------------------------------------------------------------ Modal, toast, skeleton, spinner, empty */
export function Modal({ open, title, onClose, children, width }) {
  const ref = useRef(null);
  const titleId = useId();
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.activeElement;
    ref.current && ref.current.focus();
    const onKey = (e) => { if (e.key === 'Escape' && onClose) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); prev && prev.focus && prev.focus(); };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="bv-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && onClose) onClose(); }}>
      <div className="bv-modal" role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} ref={ref} style={width ? { width } : undefined}>
        <div className="bv-modal__head">
          <h2 className="bv-modal__title" id={titleId}>{title}</h2>
          {onClose && <button type="button" className="bv-modal__close" aria-label={i18n.t('bv:common.close')} onClick={onClose}><Icon name="x" /></button>}
        </div>
        {children}
      </div>
    </div>
  );
}

export function Toast({ icon = 'print', color = 'blue', title, subtitle, children, inline }) {
  return (
    <div className={cx('bv-toast', inline && 'bv-toast--inline')} role="status">
      <IconTile icon={icon} color={color} size={44} iconSize={22} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="bv-toast__title">{title}</div>
        {subtitle && <div className="bv-toast__sub">{subtitle}</div>}
        {children}
      </div>
    </div>
  );
}

export function Skeleton({ width = '100%', height = 16, radius, className }) {
  return <div className={cx('bv-skeleton', className)} style={{ width, height, borderRadius: radius }} />;
}
export function Spinner({ label }) {
  label = label || i18n.t('bv:common.loading');
  return <div className="bv-spinner" role="status" aria-label={label} />;
}
export function EmptyState({ icon = 'sparkle', title, children, action }) {
  return (
    <div className="bv-empty">
      <IconTile icon={icon} color="blue" size={64} iconSize={30} />
      <div className="bv-empty__title">{title}</div>
      {children && <div className="bv-empty__sub">{children}</div>}
      {action}
    </div>
  );
}
